import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, MailCheck, MailX, Mail, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, ComposedChart, Line,
} from "recharts";
import { adminKycEmailStats } from "@/lib/helper-kyc.functions";

export const Route = createFileRoute("/_authenticated/admin/kyc/email-stats")({
  head: () => ({
    meta: [
      { title: "KYC email deliverability — Peopels admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DashboardPage,
});

type Stats = {
  days: number;
  series: Array<{ day: string; sent: number; failed: number; pending: number; total: number; bounces: number; complaints: number; avg_attempts: number }>;
  totals: {
    total: number; sent: number; failed: number; pending: number;
    retriesNeeded: number; deliverabilityRate: number;
    bounceCount: number; complaintCount: number; bounceRate: number; complaintRate: number; avgRetryCount: number;
  };
  byDecision: Record<string, { sent: number; failed: number; pending: number }>;
  circuitAlerts: Array<{ id: string; title: string; detail: string | null; detected_at: string }>;
};

function StatCard({ label, value, sub, tone }: { label: string; value: string | number; sub?: string; tone?: "ok" | "warn" | "bad" | "neutral" }) {
  const toneCls =
    tone === "ok" ? "text-emerald-700" :
    tone === "warn" ? "text-amber-700" :
    tone === "bad" ? "text-rose-700" : "text-slate-900";
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</p>
      {sub ? <p className="mt-1 text-xs text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

function DashboardPage() {
  const fetchStats = useServerFn(adminKycEmailStats);
  const [days, setDays] = useState<7 | 30 | 90>(30);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-kyc-email-stats", days],
    queryFn: () => fetchStats({ data: { days } }),
  });
  const stats = data as Stats | undefined;

  const decisionRows = stats
    ? Object.entries(stats.byDecision).map(([k, v]) => ({ decision: k.replaceAll("_", " "), ...v }))
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin/kyc" className="text-sm text-muted-foreground hover:text-foreground">← KYC queue</Link>
            <h1 className="mt-1 text-xl font-semibold">KYC email deliverability</h1>
            <p className="text-sm text-muted-foreground">
              Trends for verification decision emails over the last {days} days.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={days === d ? "default" : "outline"}
                size="sm"
                onClick={() => setDays(d as 7 | 30 | 90)}
              >
                {d}d
              </Button>
            ))}
            <Link to="/admin/kyc/emails" className="text-sm font-medium text-sky-700 hover:underline">Failure log →</Link>
          </div>
        </div>

        {isLoading || !stats ? (
          <div className="py-24 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-5">
              <StatCard label="Total" value={stats.totals.total} sub={`${days}d`} />
              <StatCard label="Delivered" value={stats.totals.sent} tone="ok" sub={`${stats.totals.deliverabilityRate}% rate`} />
              <StatCard label="Failed" value={stats.totals.failed} tone={stats.totals.failed > 0 ? "bad" : "neutral"} />
              <StatCard label="Pending" value={stats.totals.pending} tone={stats.totals.pending > 0 ? "warn" : "neutral"} />
              <StatCard label="Needed retry" value={stats.totals.retriesNeeded} sub="≥ 2 attempts" tone={stats.totals.retriesNeeded > 0 ? "warn" : "neutral"} />
            </div>
            <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard label="Bounce rate" value={`${stats.totals.bounceRate}%`} sub={`${stats.totals.bounceCount} bounce${stats.totals.bounceCount === 1 ? "" : "s"}`} tone={stats.totals.bounceRate >= 2 ? "bad" : stats.totals.bounceRate > 0 ? "warn" : "ok"} />
              <StatCard label="Complaint rate" value={`${stats.totals.complaintRate}%`} sub={`${stats.totals.complaintCount} complaint${stats.totals.complaintCount === 1 ? "" : "s"}`} tone={stats.totals.complaintRate >= 0.1 ? "bad" : stats.totals.complaintRate > 0 ? "warn" : "ok"} />
              <StatCard label="Avg retries" value={stats.totals.avgRetryCount} sub="per delivery" tone={stats.totals.avgRetryCount >= 2 ? "warn" : "neutral"} />
              <StatCard label="Open circuit breakers" value={stats.circuitAlerts.length} sub={stats.circuitAlerts.length ? "needs attention" : "all clear"} tone={stats.circuitAlerts.length ? "bad" : "ok"} />
            </div>

            {stats.circuitAlerts.length > 0 ? (
              <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{stats.circuitAlerts.length} circuit breaker{stats.circuitAlerts.length === 1 ? "" : "s"} open — retries paused.</span>
                </div>
                <Link to="/admin/kyc/emails" className="font-semibold underline">View &amp; acknowledge</Link>
              </div>
            ) : stats.totals.failed > 0 ? (
              <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{stats.totals.failed} email{stats.totals.failed === 1 ? "" : "s"} failed after exhausting all retries.</span>
                </div>
                <Link to="/admin/kyc/emails" className="font-semibold underline">Open failure log</Link>
              </div>
            ) : null}

            <div className="mb-6 rounded-2xl border bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold">Delivery over time</h2>
                {isFetching ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
              </div>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.series} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => String(d).slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="sent" stackId="1" stroke="#059669" fill="#a7f3d0" name="Sent" />
                    <Area type="monotone" dataKey="pending" stackId="1" stroke="#d97706" fill="#fde68a" name="Pending" />
                    <Area type="monotone" dataKey="failed" stackId="1" stroke="#dc2626" fill="#fecaca" name="Failed" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mb-6 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">Bounces &amp; complaints over time</h2>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.series} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => String(d).slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="bounces" fill="#f97316" name="Bounces" />
                      <Bar dataKey="complaints" fill="#7c3aed" name="Complaints" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">Average retry count over time</h2>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={stats.series} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => String(d).slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="avg_attempts" stroke="#0ea5e9" strokeWidth={2} name="Avg attempts" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">By decision type</h2>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={decisionRows} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="decision" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="sent" stackId="a" fill="#10b981" name="Sent" />
                      <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                      <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">Daily totals</h2>
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="py-2 text-left">Day</th>
                        <th className="py-2 text-right"><MailCheck className="ml-auto h-3.5 w-3.5 text-emerald-600" /></th>
                        <th className="py-2 text-right"><Mail className="ml-auto h-3.5 w-3.5 text-amber-600" /></th>
                        <th className="py-2 text-right"><MailX className="ml-auto h-3.5 w-3.5 text-rose-600" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...stats.series].reverse().map((d) => (
                        <tr key={d.day} className="border-t">
                          <td className="py-1.5 text-xs">{d.day}</td>
                          <td className="py-1.5 text-right text-xs text-emerald-700">{d.sent}</td>
                          <td className="py-1.5 text-right text-xs text-amber-700">{d.pending}</td>
                          <td className="py-1.5 text-right text-xs text-rose-700">{d.failed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
